/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { RichTextComponent } from '@msdyn365-commerce/core';
import { AttributeValue, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import CommerceAttributesParser, { CommerceValueTypes, ParsedValue } from '../../Utilities/commerce-attributes-parser';
import CallToAction from './../../components/call-to-action';
import { IPdpAttributesData } from './pdp-attributes.data';
import { IAttributesData, IPdpAttributesProps } from './pdp-attributes.props.autogenerated';

/**
 * PdpAttributes class renders a preconfigured list of attributes related to the
 * currentproducts page.
 *
 * PdpAttributes component
 * @extends {React.Component<IPdpAttributesProps<IPdpAttributesData>>}
 */
class PdpAttributes extends React.Component<IPdpAttributesProps<IPdpAttributesData>> {
    private _parser: CommerceAttributesParser | undefined;
    private _attributesCollected: boolean = false;

    constructor(props: IPdpAttributesProps<IPdpAttributesData>) {
        super(props);
    }

    public render(): JSX.Element | null {
        const simpleProductData = this.props.data.product.result;
        const productData: IPdpAttributesData = this.props.data;
        if (
            !(this.props.data.productSpecificationData && this.props.data.productSpecificationData.result) ||
            !(this.props.data.product && this.props.data.product.result)
        ) {
            return null;
        }

        if (!this._attributesCollected) {

            this._parser = new CommerceAttributesParser({
                attributeValues: productData.productSpecificationData.result,
                psuedoAttributeValues: this._getPsuedoSimpleAttributes(simpleProductData!)
            });
        }

        const attributes: [] = this._attributesListRender.props.children;
        if (attributes && attributes.every(element => element === null)) {
            return null;
        }

        return (
            <div className={`pdp-attributes-list ${this.props.config.cssClass || ''}`}>
                {this._attributesListRender}
            </div>
        );
    }

    private _getPsuedoSimpleAttributes(simpleProduct: SimpleProduct): AttributeValue[] {
        const attributeArray: AttributeValue[] = [];

        this.props.config.attributes.map((attr) => {
            if (simpleProduct[attr.attributeName]) {
                attributeArray.push({
                    Name: attr.attributeName,
                    DataTypeValue: 5, // everything should work fine as a string
                    TextValue: simpleProduct[attr.attributeName]
                });
            }
        });

        return attributeArray;
    }

    /**
     * returns JSX of the lists render
     */
    private get _attributesListRender(): JSX.Element {
        return (
            <React.Fragment>
                {this.props.config.attributes.map((attr, pos) => {
                    return <React.Fragment key={pos}>
                        {this._renderAttribute(attr, pos)}
                    </React.Fragment>;
                })}
            </React.Fragment>
        );
    }

    /**
     * returns JSX of a specific attribute render
     *
     * @param cmsAttribute attribute configured by author
     * @param renderPosition position to use for styling
     */
    private _renderAttribute(cmsAttribute: IAttributesData, renderPosition: number): JSX.Element | null {
        const attribute = this._parseAttribute(cmsAttribute.attributeName);
        // if attribute doesn't exist, if attribute exists but has no value
        if (!attribute || !attribute.value) { return null; }

        switch (attribute.type) {
            case 'STRING':
                // tslint:disable-next-line:no-http-string
                if (attribute.value.toString().startsWith('https://' || 'http://')) {
                    return this._urlRender(attribute as ParsedValue<string>, cmsAttribute);
                }
                return this._plaintextRender(attribute as ParsedValue<string>, cmsAttribute, true);
            case 'NUMBER':
                return this._plaintextRender(attribute as ParsedValue<number>, cmsAttribute);
            case 'CURRENCY':
                return this._plaintextRender(attribute as ParsedValue<number>, cmsAttribute);
            case 'DATE':
                return this._dateRender(attribute as ParsedValue<string>, cmsAttribute);
            case 'BOOLEAN':
                return this._booleanRender(attribute as ParsedValue<boolean>, cmsAttribute);
            default:
                return null;
        }
    }

    private _plaintextRender(text: ParsedValue<string | number>, cmsAttribute: IAttributesData, richText: boolean = false): JSX.Element | null {
        const textDisplay = (richText)
            ? (
                <RichTextComponent text={`${text.value}${cmsAttribute.suffixText || ''}`} />
            ) : (
                <span>{text.value}{cmsAttribute.suffixText || ''}</span>
            );

        return this._renderCell(
            textDisplay,
            cmsAttribute
        );
    }

    private _dateRender(date: ParsedValue<string>, cmsAttribute: IAttributesData): JSX.Element {
        return this._renderCell(
            <span>{new Date(date.value!).toUTCString()}{cmsAttribute.suffixText}</span>,
            cmsAttribute
        );
    }

    private _booleanRender(bool: ParsedValue<boolean>, cmsAttribute: IAttributesData): JSX.Element {
        const booleanDisplay = (bool.value) ? <span>Yes</span> : <span>No</span>;
        return this._renderCell(
            booleanDisplay,
            cmsAttribute
        );
    }

    private _urlRender(url: ParsedValue<string>, cmsAttribute: IAttributesData): JSX.Element {
        return this._renderCell(
            <span>
                <CallToAction
                    className='product-attribute-download-cta'
                    title={cmsAttribute.labelOverride || url.value!}
                    color='link'
                    href={url.value!}
                    layout='noGlyph'
                    openInNewTab={true}
                    aria-label={`Download ${cmsAttribute.labelOverride || url.value!}`}
                />
            </span>,
            cmsAttribute
        );
    }

    private _renderCell(cellBody: JSX.Element, cmsAttribute: IAttributesData): JSX.Element {
        let attributeName = cmsAttribute.attributeName;
        if (cmsAttribute.labelOverride) {
            attributeName = cmsAttribute.labelOverride;
        }
        return (
            <div className={`pdp-attribute ${cmsAttribute.attributeClass || ''}`}>
                {!cmsAttribute.noLabel && <span className='pdp-attribute-title'>{attributeName}</span>}
                <div className='pdp-attribute-cell'>
                    {cellBody}
                </div>
            </div>
        );
    }

    /**
     * Parses and returns the attribute value queried
     *
     * @param name name of the attribute to get
     */
    private _parseAttribute(name: string): ParsedValue<CommerceValueTypes> | undefined {
        return this._parser!.get(name);
    }
}

export default PdpAttributes;
